import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AdvancedMarker, ColorScheme, Map as GoogleMap, } from '@vis.gl/react-google-maps';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGeoPosition } from '@repo/geo-position';
import { useMapCanvas } from '../hooks/useMapCanvas';
import { googleCoordinates } from '../utils/googleCoordinates';
import { getDefaultMapCenter } from '../utils/getDefaultMapCenter';
import { toGeoCoordinates } from '../utils/toGeoCoordinates';
import AreaPolygon from './AreaPolygon';
import GeoPositionPointer from './svgs/GeoPositionPointer';
import Markers from './Markers';
import MapController from './MapController';
import OffScreenIndicatorOverlay from './OffScreenIndicatorOverlay';
export default function MapRenderer({ mapNodeId, googleMapsId, isDark, testId, initialCoordinates, fixedCoordinates, allMarkers = [], areas = [], pins = [], zoomControl = false, initialZoom = 14, mapType, gestureHandling, safeAreaInsets, onParkingClick, onMapPan, onUserPanOrZoom, onMapClick, onAreaChange, onAreaVertexClick, onAreaClick, onPinClick, }) {
    const { isKnown, position, heading } = useGeoPosition();
    const drawnMarkers = useMemo(() => allMarkers.filter((marker) => !marker.hidden), [allMarkers]);
    const { markers, fitToCluster } = useMapCanvas({ mapNodeId, allMarkers: drawnMarkers });
    const [visibleBounds, setVisibleBounds] = useState(undefined);
    const [visibleZoom, setVisibleZoom] = useState(undefined);
    const mapContainerRef = useRef(null);
    const hasPosition = !!(isKnown && position);
    const userPosition = hasPosition ? googleCoordinates(position) : undefined;
    const defaultCenter = getDefaultMapCenter({
        fixedCoordinates,
        initialCoordinates,
        userPosition,
    });
    // Fire onUserPanOrZoom only on genuine user camera gestures: `dragstart`
    // covers panning and mobile pinch-zoom, while a capture-phase wheel listener
    // covers desktop scroll-zoom. Programmatic camera moves (repan/fitToCluster)
    // never emit these, so they never false-trigger.
    useEffect(() => {
        const container = mapContainerRef.current;
        if (!container || !onUserPanOrZoom)
            return;
        const handleWheel = () => onUserPanOrZoom();
        container.addEventListener('wheel', handleWheel, { capture: true, passive: true });
        return () => container.removeEventListener('wheel', handleWheel, { capture: true });
    }, [onUserPanOrZoom]);
    const handleMapDragStart = () => {
        onUserPanOrZoom?.();
    };
    const handleClusterClick = (cluster) => {
        fitToCluster(cluster);
    };
    const handleParkingClick = (marker) => {
        if (onParkingClick) {
            onParkingClick(marker);
        }
    };
    const handleMapPan = (event) => {
        const bounds = event.map.getBounds();
        const center = event.map.getCenter();
        const zoom = event.map.getZoom();
        if (!bounds || !center || zoom === undefined)
            return;
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        const geoBounds = {
            north: northEast.lat(),
            south: southWest.lat(),
            east: northEast.lng(),
            west: southWest.lng(),
        };
        setVisibleBounds(geoBounds);
        setVisibleZoom(zoom);
        if (!onMapPan)
            return;
        onMapPan({
            center: { latitude: center.lat(), longitude: center.lng() },
            bounds: geoBounds,
            zoom,
        });
    };
    const handleMapClick = (event) => {
        if (!onMapClick || !event.detail.latLng)
            return;
        onMapClick(toGeoCoordinates(event.detail.latLng));
    };
    const handlePinClick = (key) => {
        onPinClick?.(key);
    };
    const handleMapIdle = (event) => {
        const bounds = event.map.getBounds();
        const zoom = event.map.getZoom();
        if (!bounds || zoom === undefined)
            return;
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        setVisibleBounds({
            north: northEast.lat(),
            south: southWest.lat(),
            east: northEast.lng(),
            west: southWest.lng(),
        });
        setVisibleZoom(zoom);
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "relative h-full w-full", ref: mapContainerRef, children: [_jsxs(GoogleMap, { id: mapNodeId, mapId: googleMapsId, className: "h-full w-full", colorScheme: isDark ? ColorScheme.DARK : ColorScheme.LIGHT, defaultCenter: defaultCenter, defaultZoom: initialZoom, gestureHandling: gestureHandling ?? (fixedCoordinates ? 'none' : 'greedy'), disableDefaultUI: true, mapTypeId: mapType, zoomControl: zoomControl, onCameraChanged: handleMapPan, onDragstart: handleMapDragStart, onIdle: handleMapIdle, onClick: handleMapClick, clickableIcons: !onMapClick, children: [pins.map((pin) => (_jsx(AdvancedMarker, { position: googleCoordinates(pin.position), zIndex: pin.zIndex, ...(onPinClick && { onClick: () => handlePinClick(pin.key) }), ...(pin.anchor === 'center' && { anchorLeft: '-50%', anchorTop: '-50%' }), children: pin.content }, pin.key))), areas.map((area) => (_jsx(AreaPolygon, { area: area, onChange: onAreaChange, onVertexClick: onAreaVertexClick, onClick: onMapClick, onAreaClick: onAreaClick }, area.key))), _jsx(Markers, { markers: markers, testId: testId, onClusterClick: handleClusterClick, onParkingClick: handleParkingClick }), hasPosition && (_jsx(AdvancedMarker, { position: userPosition, anchorLeft: "-50%", anchorTop: "-50%", children: _jsx(GeoPositionPointer, { testId: testId.append('pointer'), heading: heading }) }))] }), _jsx(OffScreenIndicatorOverlay, { testId: testId, mapNodeId: mapNodeId, bounds: visibleBounds, zoom: visibleZoom, allMarkers: allMarkers, safeAreaInsets: safeAreaInsets })] }), _jsx(MapController, { mapNodeId: mapNodeId })] }));
}
